import React, { useEffect, useState } from 'react';
import { Globe2, Mic } from 'lucide-react';

// "Built around you": a level meter (the speaking test students take before
// their first class) and live clocks for three cities (every time on the site
// is shown in your own time zone). Animations are CSS in landing-motion.css.
const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CITIES = [
  { name: 'Manila', tz: 'Asia/Manila' },
  { name: 'Tokyo', tz: 'Asia/Tokyo' },
  { name: 'New York', tz: 'America/New_York' },
];

const cityTime = (tz) => {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  const h = Number(get('hour')) % 12; const m = Number(get('minute'));
  return { label: `${get('hour')}:${get('minute')} ${get('dayPeriod')}`, hourDeg: h * 30 + m * 0.5, minDeg: m * 6 };
};

const Clock = ({ city }) => {
  const [t, setT] = useState(() => cityTime(city.tz));
  useEffect(() => { const id = setInterval(() => setT(cityTime(city.tz)), 30000); return () => clearInterval(id); }, [city.tz]);
  return (
    <div className="text-center">
      <svg viewBox="0 0 80 80" className="w-20 h-20 mx-auto" aria-hidden="true">
        <circle cx="40" cy="40" r="36" className="fill-white dark:fill-slate-800 stroke-primary/30 dark:stroke-gold-300/30" strokeWidth="2" />
        {[...Array(12)].map((_, i) => (
          <line key={i} x1="40" y1="8" x2="40" y2={i % 3 ? 11 : 13} className="stroke-slate-400" strokeWidth={i % 3 ? 1 : 2} transform={`rotate(${i * 30} 40 40)`} />
        ))}
        <g transform={`rotate(${t.hourDeg} 40 40)`}><rect x="38.5" y="22" width="3" height="18" rx="1.5" className="fill-navy dark:fill-white" /></g>
        <g transform={`rotate(${t.minDeg} 40 40)`}><rect className="fill-primary dark:fill-primary-300" x="39" y="12" width="2" height="28" rx="1" /></g>
        <rect className="clock-hand hand-sec fill-gold-500" x="39.5" y="10" width="1" height="30" rx="0.5" style={{ animationDelay: `-${new Date().getSeconds()}s` }} />
        <circle cx="40" cy="40" r="2.5" className="fill-gold-500" />
      </svg>
      <p className="mt-2 text-sm font-semibold text-navy dark:text-white">{city.name}</p>
      <p className="text-xs text-text-muted tabular-nums">{t.label}</p>
    </div>
  );
};

const BuiltAroundYou = () => (
  <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8" data-testid="built-around-you" data-motion-zone>
    <div className="max-w-[1440px] mx-auto">
      <h2 className="reveal text-3xl sm:text-4xl font-heading font-bold text-navy dark:text-white mb-8 text-center">Built around you<span className="draw-line block h-1 w-20 mx-auto rounded-full bg-gradient-to-r from-primary to-gold-400 mt-3" aria-hidden="true" /></h2>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Level meter */}
        <div data-tilt className="reveal rounded-3xl bg-white/80 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 shadow-card p-6 sm:p-8" data-testid="level-meter">
          <p className="flex items-center gap-2 text-sm font-semibold text-gold-600 dark:text-gold-300 mb-1"><Mic className="w-4 h-4" /> Know your level</p>
          <h3 className="text-xl font-heading font-bold text-navy dark:text-white mb-2">A short speaking test before your first class</h3>
          <p className="text-sm text-text-secondary mb-5">Your teacher listens first, so lessons start at the right level for you.</p>
          <svg viewBox="0 0 200 120" className="w-full max-w-xs mx-auto block" aria-hidden="true">
            <defs>
              <linearGradient id="gaugeGrad" x1="0" x2="1"><stop offset="0%" stopColor="#3b82f6" /><stop offset="100%" stopColor="#facc15" /></linearGradient>
            </defs>
            <path d="M20 100 A80 80 0 0 1 180 100" fill="none" className="stroke-slate-200 dark:stroke-white/10" strokeWidth="14" strokeLinecap="round" />
            <path className="gauge-arc" pathLength="1" d="M20 100 A80 80 0 0 1 180 100" fill="none" stroke="url(#gaugeGrad)" strokeWidth="14" strokeLinecap="round" />
            {LEVELS.map((l, i) => {
              const a = (-75 + i * 30) * (Math.PI / 180);
              return <text key={l} x={100 + Math.sin(a) * 63} y={100 - Math.cos(a) * 63 + 4} textAnchor="middle" className="fill-slate-400 text-[9px] font-semibold">{l}</text>;
            })}
            <g className="gauge-needle">
              <line x1="100" y1="100" x2="100" y2="52" className="stroke-navy dark:stroke-white" strokeWidth="3" strokeLinecap="round" />
            </g>
            <circle cx="100" cy="100" r="7" className="fill-gold-500" />
          </svg>
          <div className="flex justify-center gap-2 mt-3">
            {LEVELS.map((l, i) => (
              <span key={l} className={`level-chip ${i <= 3 ? 'on' : ''} rounded-full px-2.5 py-1 text-xs font-bold ${i <= 3 ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-white/10 text-text-muted'}`}
                style={{ transitionDelay: `${0.5 + i * 0.3}s` }}>{l}</span>
            ))}
          </div>
        </div>

        {/* Time zones */}
        <div data-tilt className="reveal rounded-3xl bg-white/80 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 shadow-card p-6 sm:p-8" data-testid="timezones">
          <p className="flex items-center gap-2 text-sm font-semibold text-gold-600 dark:text-gold-300 mb-1"><Globe2 className="w-4 h-4" /> Classes in your time zone</p>
          <h3 className="text-xl font-heading font-bold text-navy dark:text-white mb-2">Your time, wherever you are</h3>
          <p className="text-sm text-text-secondary mb-5">Bookings, reminders and emails always show your own local time.</p>
          <div className="relative">
            <svg viewBox="0 0 300 60" className="absolute inset-x-0 -top-3 w-full h-16 pointer-events-none" preserveAspectRatio="none" aria-hidden="true">
              <path id="tzArc" d="M50 50 Q 150 -10 250 50" fill="none" className="stroke-primary/40 dark:stroke-gold-300/40" strokeWidth="1.5" strokeDasharray="4 5" vectorEffect="non-scaling-stroke" />
              <circle r="4" className="fill-gold-500">
                <animateMotion dur="4.5s" repeatCount="indefinite" path="M50 50 Q 150 -10 250 50" />
              </circle>
            </svg>
            <div className="grid grid-cols-3 gap-2 pt-10">
              {CITIES.map((c) => <Clock key={c.tz} city={c} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default BuiltAroundYou;
